import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { getActiveOrders, updateOrder } from '../api/restaurant';
import { getMenuCategories } from '../api/api';
import { Clock, Check, ChefHat, Printer, DollarSign, CreditCard, Banknote, Building, Receipt, ArrowRight, Undo2, Bell, XCircle, Wine, UtensilsCrossed, Mail, FileText, Beer, Pencil, Plus, Minus, Trash2, Wallet, CircleDollarSign } from 'lucide-react';
import api from '../api/axios';

const ICON_MAP = {
  CreditCard: CreditCard,
  Banknote: Banknote,
  Building: Building,
  Receipt: Receipt,
  DollarSign: DollarSign,
  Wallet: Wallet,
  Coins: CircleDollarSign,
  BadgeEuro: DollarSign,
};

const FALLBACK_METHODS = [
  { method_id: 'CB', label: 'Carte Bancaire', icon: 'CreditCard', is_cash: false },
  { method_id: 'Espece', label: 'Especes', icon: 'Banknote', is_cash: true },
  { method_id: 'Virement', label: 'Virement', icon: 'Building', is_cash: false },
  { method_id: 'Cheque', label: 'Cheque', icon: 'Receipt', is_cash: false },
  { method_id: 'Cheque Restaurant', label: 'Cheque Resto', icon: 'Receipt', is_cash: false },
];

const STATUSES = ['En attente', 'En préparation', 'Prête', 'Servie'];
const STATUS_COLORS = {
  'En attente': 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
  'En préparation': 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200',
  'Prête': 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200',
  'Servie': 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200',
};

const getTicketStyle = (fontSize = 14) => {
  const legacy = { small: 11, normal: 14, large: 18 };
  const parsed = Number(fontSize);
  const baseSize = Math.min(32, Math.max(11, Number.isFinite(parsed) ? parsed : (legacy[fontSize] || 14)));
  return `<style>
  @page{size:70mm auto;margin:0}
  *{margin:0;padding:0;box-sizing:border-box;font-weight:900 !important}
  body{font-family:'Courier New',Courier,monospace;width:66mm;margin:1mm 2mm;font-size:${baseSize}px;color:#000;line-height:1.3}
  .header{text-align:center;padding-bottom:3px;border-bottom:2px double #000}
  .logo{display:block;width:30mm;max-height:25mm;object-fit:contain;margin:0 auto 3px}
  .station{font-size:${baseSize + 7}px;letter-spacing:2px;border:2px solid #000;padding:3px 6px;display:inline-block;margin-bottom:3px}
  .sep{border:none;border-top:1px dashed #000;margin:3px 0}
  .meta{font-size:${Math.max(8, baseSize - 1)}px;line-height:1.4;padding:2px 0}
  .cat-header{font-size:${baseSize}px;text-align:center;padding:3px 0 1px;border-bottom:1px solid #000;letter-spacing:1px;text-decoration:underline}
  .items-table{width:100%;border-collapse:collapse;margin:3px 0}
  .item-row td{padding:2px 0;border-bottom:1px dotted #000;vertical-align:top}
  .qty{width:18px;font-size:${baseSize + 1}px}
  .name{font-size:${baseSize + 1}px;word-break:break-word}
  .price{width:42px;font-size:${baseSize}px;text-align:right;white-space:nowrap}
  .detail{font-size:${Math.max(8, baseSize - 1)}px;padding:0 0 2px 0;border:none !important}
  .subtotal{text-align:right;font-size:${baseSize + 1}px;padding:3px 0;border-top:1px solid #000}
  .total{text-align:right;font-size:${baseSize + 3}px;padding:5px 0;border-top:2px double #000;margin-top:3px}
</style>`;
};

const OrdersManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [payDialog, setPayDialog] = useState(false);
  const [payMode, setPayMode] = useState('all');
  const [payingOrders, setPayingOrders] = useState([]);
  const [payingOrder, setPayingOrder] = useState(null);
  const [payMethod, setPayMethod] = useState(null);
  const [amountGiven, setAmountGiven] = useState('');
  const [processing, setProcessing] = useState(false);
  const [mixedAmounts, setMixedAmounts] = useState({});
  const staffRole = localStorage.getItem('staffRole') || '';
  const [billRequests, setBillRequests] = useState({});
  const [waiterCalls, setWaiterCalls] = useState({});
  const [allTables, setAllTables] = useState([]);
  // Simple invoice
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [invoiceData, setInvoiceData] = useState({ email: '', designation: '', amount: 0, table_number: 0, client_name: '' });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  // Dynamic payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [printerConfig, setPrinterConfig] = useState({ font_size: 14, print_logo: true });
  // Edit order item
  const [editDialog, setEditDialog] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editItemIdx, setEditItemIdx] = useState(null);
  const [editQty, setEditQty] = useState(1);
  const [editNotes, setEditNotes] = useState('');
  const [showReplaceItem, setShowReplaceItem] = useState(false);
  const [replaceCat, setReplaceCat] = useState(null);
  // Add items to order
  const [addItemsDialog, setAddItemsDialog] = useState(false);
  const [addToOrder, setAddToOrder] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedAddCat, setSelectedAddCat] = useState(null);
  const [addingItem, setAddingItem] = useState(false);

  const getAvailableStatuses = (currentStatus) => {
    const idx = STATUSES.indexOf(currentStatus);
    if (idx === -1) return [];
    const priv = staffRole === 'admin' || staffRole === 'webmaster';
    if (priv) return STATUSES.filter(s => s !== currentStatus);
    return idx < STATUSES.length - 1 ? [STATUSES[idx + 1]] : [];
  };

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchOrders(); fetchBillRequests(); fetchMenu(); fetchPaymentMethods();
    api.get('/printer/config').then(({ data }) => setPrinterConfig(data)).catch(() => {});
    const interval = setInterval(() => { fetchOrders(); fetchBillRequests(); }, 10000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchOrders = async () => {
    try { const data = await getActiveOrders(); setOrders(data); }
    catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const fetchMenu = async () => {
    try {
      const [menuData, drinksRes] = await Promise.all([
        getMenuCategories(),
        api.get('/menu/drinks').then(r => r.data).catch(() => [])
      ]);
      const drinkCats = drinksRes.map(c => ({ ...c, isDrink: true }));
      setMenuCategories([...menuData, ...drinkCats]);
    } catch {}
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await api.get('/payment-methods');
      const active = res.data.filter(m => m.active);
      setPaymentMethods(active.length > 0 ? active : FALLBACK_METHODS);
    } catch {
      setPaymentMethods(FALLBACK_METHODS);
    }
  };

  const activeMethods = paymentMethods;
  const cashMethodIds = activeMethods.filter(m => m.is_cash).map(m => m.method_id);

  const fetchBillRequests = async () => {
    try {
      const res = await api.get('/tables');
      setAllTables(res.data);
      const bm = {}, wm = {};
      res.data.forEach(t => { if (t.bill_requested) bm[t.table_number] = true; if (t.waiter_called) wm[t.table_number] = true; });
      setBillRequests(bm); setWaiterCalls(wm);
    } catch {}
  };

  const clearBillRequest = async (n) => { try { await api.post(`/tables/${n}/clear-bill-request`); setBillRequests(p => { const c = { ...p }; delete c[n]; return c; }); } catch {} };
  const clearWaiterCall = async (n) => { try { await api.post(`/tables/${n}/clear-waiter-call`); setWaiterCalls(p => { const c = { ...p }; delete c[n]; return c; }); } catch {} };

  const handleStatusChange = async (id, s) => { try { await updateOrder(id, { status: s }); await fetchOrders(); toast({ title: 'Statut mis a jour' }); } catch { toast({ title: 'Erreur', variant: 'destructive' }); } };
  const handleCancelOrder = async (id) => { if (!window.confirm('Annuler cette commande ?')) return; try { await updateOrder(id, { status: 'Annulee' }); await fetchOrders(); toast({ title: 'Commande annulee' }); } catch { toast({ title: 'Erreur', variant: 'destructive' }); } };

  const handleItemStatusChange = async (orderId, itemIndex, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/items/${itemIndex}/status`, { status: newStatus });
      await fetchOrders();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  // Merge map
  const mergeMap = {};
  allTables.forEach(t => { if (t.merged_with?.length > 1) { const k = [...t.merged_with].sort((a, b) => a - b).join('+'); t.merged_with.forEach(n => { mergeMap[n] = k; }); } });
  const groupedByTable = {};
  orders.forEach(o => { const k = mergeMap[o.table_number] || String(o.table_number); if (!groupedByTable[k]) groupedByTable[k] = []; groupedByTable[k].push(o); });
  const filteredTables = Object.entries(groupedByTable).filter(([_, to]) => filter === 'all' || to.some(o => o.status === filter));

  // Payment
  const openPayAllDialog = (to) => { const s = to.filter(o => o.status === 'Servie'); if (!s.length) { toast({ title: 'Aucune commande servie', variant: 'destructive' }); return; } setPayMode('all'); setPayingOrders(s); setPayingOrder(null); setPayMethod(null); setAmountGiven(''); setMixedAmounts({}); setPayDialog(true); };
  const openPaySingleDialog = (o) => { setPayMode('single'); setPayingOrder(o); setPayingOrders([]); setPayMethod(null); setAmountGiven(''); setMixedAmounts({}); setPayDialog(true); };

  const currentTotal = payMode === 'all' ? payingOrders.reduce((s, o) => s + parseFloat(o.total), 0) : payingOrder ? parseFloat(payingOrder.total) : 0;
  const given = amountGiven ? parseFloat(amountGiven) : 0;
  const changeDue = cashMethodIds.includes(payMethod) && given > currentTotal ? (given - currentTotal).toFixed(2) : null;
  const mixedTotal = Object.values(mixedAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const mixedRemaining = (currentTotal - mixedTotal).toFixed(2);

  const handlePayment = async () => {
    if (!payMethod) { toast({ title: 'Selectionnez un mode', variant: 'destructive' }); return; }
    if (payMethod === 'Mixte' && mixedTotal < currentTotal - 0.01) { toast({ title: `Il reste ${mixedRemaining} EUR a ventiler`, variant: 'destructive' }); return; }
    setProcessing(true);
    try {
      const ordersToProcess = payMode === 'all' ? payingOrders : [payingOrder];
      const mixedDetails = payMethod === 'Mixte' ? Object.entries(mixedAmounts).filter(([_, v]) => parseFloat(v) > 0).map(([m, v]) => ({ method: m, amount: parseFloat(v) })) : null;
      for (const order of ordersToProcess) {
        await api.post('/payments', {
          order_id: order.id, amount: parseFloat(order.total), method: payMethod,
          amount_given: cashMethodIds.includes(payMethod) ? (given || parseFloat(order.total)) : parseFloat(order.total),
          change_due: cashMethodIds.includes(payMethod) && changeDue ? parseFloat(changeDue) : 0,
          mixed_details: mixedDetails
        });
        await updateOrder(order.id, { status: 'Payée' });
      }
      const desc = payMethod === 'Mixte' ? mixedDetails.map(d => `${d.method}: ${d.amount.toFixed(2)}`).join(' + ') : `${payMethod} - ${currentTotal.toFixed(2)} EUR`;
      toast({ title: 'Paiement enregistre', description: desc });
      setPayDialog(false); await fetchOrders();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setProcessing(false); }
  };

  // Print single order ticket
  const printTicket = (order, type) => {
    const dateStr = new Date(order.created_at).toLocaleString('fr-FR', { timeZone: 'America/Cayenne' });
    const items = type === 'boissons' ? order.items.filter(i => i.type === 'boisson') : type === 'cuisine' ? order.items.filter(i => i.type !== 'boisson') : order.items;
    if (!items.length) { toast({ title: `Aucun article ${type}` }); return; }
    _doPrint(items, type === 'boissons' ? 'BOISSONS' : 'CUISINE', `Table: ${order.table_number} | Client: ${order.customer_name || '-'}`, dateStr);
  };

  // Print ALL items for a table (all orders combined)
  const printTableTicket = (tableOrders, type) => {
    const allItems = [];
    tableOrders.forEach(o => o.items.forEach(i => allItems.push(i)));
    const items = type === 'boissons' ? allItems.filter(i => i.type === 'boisson') : allItems.filter(i => i.type !== 'boisson');
    if (!items.length) { toast({ title: `Aucun article ${type} pour cette table` }); return; }
    const tn = tableOrders[0]?.table_number || '?';
    const dateStr = new Date().toLocaleString('fr-FR', { timeZone: 'America/Cayenne' });
    _doPrint(items, type === 'boissons' ? 'BOISSONS TABLE' : 'CUISINE TABLE', `Table: ${tn}`, dateStr);
  };

  // Print pre-payment receipt (ticket de caisse)
  const printPreReceipt = (tableOrders) => {
    const allItems = [];
    tableOrders.forEach(o => o.items.forEach(i => allItems.push(i)));
    if (!allItems.length) return;
    const tn = tableOrders[0]?.table_number || '?';
    const dateStr = new Date().toLocaleString('fr-FR', { timeZone: 'America/Cayenne' });
    const total = tableOrders.reduce((s, o) => s + parseFloat(o.total), 0).toFixed(2);

    const groups = {};
    allItems.forEach(i => { const cat = i.category_name || 'Autres'; if (!groups[cat]) groups[cat] = []; groups[cat].push(i); });
    let itemsHtml = '';
    Object.keys(groups).forEach(cat => {
      if (Object.keys(groups).length > 1) itemsHtml += `<tr><td colspan="3" class="cat-header">${cat}</td></tr>`;
      groups[cat].forEach(i => {
        const sub = (parseFloat(i.price) * i.quantity).toFixed(2);
        itemsHtml += `<tr class="item-row"><td class="qty">${i.quantity}x</td><td class="name">${i.name}</td><td class="price">${sub}</td></tr>`;
        if (i.cuisson) itemsHtml += `<tr><td></td><td class="detail" colspan="2">Cuisson: ${i.cuisson}</td></tr>`;
        if (i.sauce) itemsHtml += `<tr><td></td><td class="detail" colspan="2">Sauce: ${i.sauce}</td></tr>`;
        if (i.accompagnements?.length) itemsHtml += `<tr><td></td><td class="detail" colspan="2">Accomp: ${i.accompagnements.join(', ')}</td></tr>`;
      });
    });

    const w = window.open('', '_blank', 'width=302,height=600');
    const cashLogo = printerConfig.print_logo !== false
      ? `<img class="logo" src="${window.location.origin}/logo-taverne.png" alt="La Taverne">`
      : '';
    w.document.write(`<!DOCTYPE html><html><head>${getTicketStyle(printerConfig.font_size)}</head><body>
      <div class="header">${cashLogo}<div class="station">ADDITION</div><div style="margin-top:2px">La Taverne</div></div>
      <hr class="sep"><div class="meta"><div><b>Table:</b> ${tn}</div><div>${dateStr}</div></div><hr class="sep">
      <table class="items-table">${itemsHtml}</table>
      <div class="total">TOTAL: ${total} EUR</div>
      <hr class="sep"><div style="text-align:center;font-size:9px;padding:3px 0">Merci de votre visite !</div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 400);
  };

  const _doPrint = (items, label, metaLine, dateStr) => {
    const groups = {};
    items.forEach(i => { const cat = i.category_name || 'Autres'; if (!groups[cat]) groups[cat] = []; groups[cat].push(i); });
    let itemsHtml = '';
    Object.keys(groups).forEach(cat => {
      if (Object.keys(groups).length > 1) itemsHtml += `<tr><td colspan="3" class="cat-header">${cat}</td></tr>`;
      groups[cat].forEach(i => {
        const sub = (parseFloat(i.price) * i.quantity).toFixed(2);
        let d = '';
        if (i.cuisson) d += `<tr><td></td><td class="detail" colspan="2">Cuisson: ${i.cuisson}</td></tr>`;
        if (i.sauce) d += `<tr><td></td><td class="detail" colspan="2">Sauce: ${i.sauce}</td></tr>`;
        if (i.accompagnements?.length) d += `<tr><td></td><td class="detail" colspan="2">Accomp: ${i.accompagnements.join(', ')}</td></tr>`;
        itemsHtml += `<tr class="item-row"><td class="qty">${i.quantity}x</td><td class="name">${i.name}</td><td class="price">${sub}</td></tr>${d}`;
      });
    });
    const subTotal = items.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0).toFixed(2);
    const w = window.open('', '_blank', 'width=302,height=600');
    w.document.write(`<!DOCTYPE html><html><head>${getTicketStyle(printerConfig.font_size)}</head><body>
      <div class="header"><div class="station">${label}</div></div><hr class="sep">
      <div class="meta"><div><b>${metaLine}</b></div><div>${dateStr}</div></div><hr class="sep">
      <table class="items-table">${itemsHtml}</table>
      <div class="subtotal">Sous-total: ${subTotal} EUR</div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 400);
  };

  // Simple invoice email
  const openInvoiceDialog = (tableOrders) => {
    const total = tableOrders.reduce((s, o) => s + parseFloat(o.total), 0);
    setInvoiceData({ email: '', designation: '', amount: total, table_number: tableOrders[0]?.table_number || 0, client_name: tableOrders[0]?.customer_name || '' });
    setInvoiceDialog(true);
  };
  const sendSimpleInvoice = async () => {
    if (!invoiceData.email || !invoiceData.designation) { toast({ title: 'Email et designation requis', variant: 'destructive' }); return; }
    setSendingInvoice(true);
    try {
      await api.post('/payments/simple-invoice', invoiceData);
      toast({ title: 'Facture envoyee', description: `A ${invoiceData.email}` });
      setInvoiceDialog(false);
    } catch { toast({ title: 'Erreur envoi', variant: 'destructive' }); }
    finally { setSendingInvoice(false); }
  };

  const getStatusColor = (s) => ({ 'En attente': 'destructive', 'En préparation': 'warning', 'Prête': 'success', 'Servie': 'default', 'Payée': 'outline' }[s] || 'default');

  // Edit item
  const openEditItem = (order, itemIdx) => {
    setEditOrder(order);
    setEditItemIdx(itemIdx);
    setEditQty(order.items[itemIdx].quantity);
    setEditNotes(order.items[itemIdx].notes || '');
    setShowReplaceItem(false);
    setEditDialog(true);
  };
  const saveEditItem = async () => {
    if (!editOrder || editItemIdx === null) return;
    try {
      if (editQty <= 0) {
        await api.delete(`/orders/${editOrder.id}/items/${editItemIdx}`);
      } else {
        await api.put(`/orders/${editOrder.id}/items/${editItemIdx}`, { quantity: editQty, notes: editNotes });
      }
      toast({ title: 'Article modifie' });
      setEditDialog(false);
      await fetchOrders();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };
  const replaceItemInOrder = async (newItem, cat) => {
    if (!editOrder || editItemIdx === null) return;
    try {
      // Delete old item
      await api.delete(`/orders/${editOrder.id}/items/${editItemIdx}`);
      // Add new item
      const itemType = cat?.isDrink ? 'boisson' : 'plat';
      const price = typeof newItem.price === 'number' ? newItem.price.toFixed(2) : String(newItem.price);
      await api.post(`/orders/${editOrder.id}/add-items`, {
        items: [{ menu_item_id: newItem.id, name: newItem.name, price, quantity: editQty || 1, type: itemType, category_name: cat?.name || '' }],
        total: price
      });
      toast({ title: 'Article remplace', description: newItem.name });
      setEditDialog(false);
      await fetchOrders();
    } catch { toast({ title: 'Erreur remplacement', variant: 'destructive' }); }
  };
  const deleteOrderItem = async (orderId, itemIdx) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/orders/${orderId}/items/${itemIdx}`);
      toast({ title: 'Article supprime' });
      await fetchOrders();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  // Add items to existing order
  const openAddItems = (order) => {
    setAddToOrder(order);
    setSelectedAddCat(menuCategories[0]?.id || null);
    setAddItemsDialog(true);
  };
  const addItemToOrder = async (item, cat) => {
    if (!addToOrder || addingItem) return;
    setAddingItem(true);
    const itemType = cat?.isDrink ? 'boisson' : 'plat';
    const price = typeof item.price === 'number' ? item.price.toFixed(2) : String(item.price);
    try {
      await api.post(`/orders/${addToOrder.id}/add-items`, {
        items: [{ menu_item_id: item.id, name: item.name, price, quantity: 1, type: itemType, category_name: cat?.name || '' }],
        total: price
      });
      toast({ title: 'Article ajoute', description: item.name });
      await fetchOrders();
    } catch { toast({ title: 'Erreur ajout', variant: 'destructive' }); }
    finally { setAddingItem(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-3 shadow-lg">
        <div className="max-w-7xl mx-auto px-3 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Commandes</h1>
            <p className="text-[10px] text-gray-300">{orders.length} commande(s) | {Object.keys(groupedByTable).length} table(s)</p>
          </div>
          <div className="flex gap-1.5">
            {staffRole !== 'serveur' && <Button onClick={() => navigate('/staff/payments')} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-[10px] h-7"><Receipt size={12} className="mr-1" /> Historique</Button>}
            <Button onClick={() => navigate('/kitchen')} size="sm" className="bg-purple-600 hover:bg-purple-700 text-[10px] h-7"><ChefHat size={12} className="mr-1" /> Cuisine</Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-[10px] h-7">Dashboard</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {['all', ...STATUSES].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-[10px] font-medium border transition-all ${filter === f ? 'bg-[#2c1810] text-white border-[#2c1810]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              {f === 'all' ? 'Toutes' : f}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filteredTables.map(([tableKey, tableOrders]) => {
            const tableTotal = tableOrders.reduce((s, o) => s + parseFloat(o.total), 0).toFixed(2);
            const hasServie = tableOrders.some(o => o.status === 'Servie');
            const isMerged = tableKey.includes('+');
            const groupNums = isMerged ? tableKey.split('+').map(Number) : [parseInt(tableKey)];
            const displayLabel = isMerged ? `Tables ${tableKey.replace(/\+/g, '+')}` : `Table ${tableKey}`;
            const shortLabel = isMerged ? `T${groupNums[0]}+` : `T${tableKey}`;
            const hasBillRequest = groupNums.some(n => billRequests[n]);
            const hasWaiterCall = groupNums.some(n => waiterCalls[n]);
            const clearGroupBill = () => groupNums.forEach(n => { if (billRequests[n]) clearBillRequest(n); });
            const clearGroupWaiter = () => groupNums.forEach(n => { if (waiterCalls[n]) clearWaiterCall(n); });

            const hasPlats = tableOrders.some(o => o.items.some(i => i.type !== 'boisson'));
            const hasBoissons = tableOrders.some(o => o.items.some(i => i.type === 'boisson'));

            return (
              <Card key={tableKey} className={`border-2 shadow-sm ${hasBillRequest || hasWaiterCall ? 'border-amber-400 ring-2 ring-amber-200' : 'border-[#d4c4b0]'}`} data-testid={`table-group-${tableKey}`}>
                <CardHeader className={`pb-2 pt-3 px-3 ${hasBillRequest || hasWaiterCall ? 'bg-amber-50' : 'bg-[#f5f0ea]'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${isMerged ? 'text-xs' : 'text-lg'} ${hasBillRequest || hasWaiterCall ? 'bg-amber-500 text-white animate-pulse' : 'bg-[#2c1810] text-white'}`}>{shortLabel}</div>
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <CardTitle className="text-base">{displayLabel}</CardTitle>
                          {isMerged && <span className="text-[8px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded font-semibold">Fusionnees</span>}
                          {hasWaiterCall && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-blue-500 text-white animate-pulse"><Bell size={8} /> SERVEUR</span>}
                          {hasBillRequest && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-500 text-white animate-pulse"><Bell size={8} /> ADDITION</span>}
                        </div>
                        <p className="text-[10px] text-gray-500">{tableOrders.length} cmd | Total: {tableTotal}&euro;</p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {hasWaiterCall && <Button onClick={clearGroupWaiter} size="sm" className="bg-blue-500 hover:bg-blue-600 text-white text-[9px] h-6 px-2"><Check size={10} /> OK</Button>}
                      {hasBillRequest && <Button onClick={clearGroupBill} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-[9px] h-6 px-2"><Check size={10} /> Vu</Button>}
                    </div>
                  </div>
                  {/* Table-level action buttons */}
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {hasPlats && <Button onClick={() => printTableTicket(tableOrders, 'cuisine')} variant="outline" size="sm" className="h-6 text-[9px] px-2 bg-orange-50 border-orange-200 hover:bg-orange-100" data-testid={`print-table-cuisine-${tableKey}`}><ChefHat size={10} className="mr-0.5" /> Ticket Cuisine Table</Button>}
                    {hasBoissons && <Button onClick={() => printTableTicket(tableOrders, 'boissons')} variant="outline" size="sm" className="h-6 text-[9px] px-2 bg-purple-50 border-purple-200 hover:bg-purple-100" data-testid={`print-table-boissons-${tableKey}`}><Wine size={10} className="mr-0.5" /> Ticket Boissons Table</Button>}
                    <Button onClick={() => printPreReceipt(tableOrders)} variant="outline" size="sm" className="h-6 text-[9px] px-2 bg-blue-50 border-blue-200 hover:bg-blue-100" data-testid={`print-receipt-${tableKey}`}><FileText size={10} className="mr-0.5" /> Ticket Caisse</Button>
                    {staffRole !== 'serveur' && <Button onClick={() => openInvoiceDialog(tableOrders)} variant="outline" size="sm" className="h-6 text-[9px] px-2 bg-green-50 border-green-200 hover:bg-green-100" data-testid={`invoice-${tableKey}`}><Mail size={10} className="mr-0.5" /> Facture Email</Button>}
                    {hasServie && staffRole !== 'serveur' && <Button onClick={() => openPayAllDialog(tableOrders)} size="sm" className="bg-green-600 hover:bg-green-700 text-[9px] h-6 px-2" data-testid={`pay-all-table-${tableKey}`}><DollarSign size={10} className="mr-0.5" /> Tout encaisser</Button>}
                  </div>
                </CardHeader>
                <CardContent className="p-2">
                  {/* Two-column layout: Plats | Boissons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {/* PLATS column */}
                    {hasPlats && (
                      <div className="border rounded-lg p-2 bg-orange-50/30 border-orange-100">
                        <div className="text-[10px] font-bold text-orange-700 uppercase tracking-wider mb-1 flex items-center gap-1"><UtensilsCrossed size={10} /> Plats</div>
                        {tableOrders.map(order => {
                          const plats = order.items.filter(i => i.type !== 'boisson');
                          if (!plats.length) return null;
                          return (
                            <div key={order.id} className="mb-1.5 last:mb-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] font-semibold">{order.customer_name || 'Client'}</span>
                                </div>
                                <span className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' })}</span>
                              </div>
                              {plats.map((item, idx) => {
                                const realIdx = order.items.indexOf(item);
                                const iStatus = item.status || 'En attente';
                                const sColor = iStatus === 'Servie' ? 'bg-blue-100 text-blue-700' : iStatus === 'Prête' ? 'bg-green-100 text-green-700' : iStatus === 'En préparation' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700';
                                const nextStatus = STATUSES[Math.min(STATUSES.indexOf(iStatus) + 1, STATUSES.length - 1)];
                                const canAdvance = iStatus !== 'Servie';
                                return (
                                  <div key={idx} className="flex items-center gap-1 text-[10px] text-gray-700 pl-2 border-l-2 border-orange-200 ml-0.5 mb-0.5">
                                    <span className="font-medium flex-1">{item.quantity}x {item.name}
                                      {item.cuisson && <span className="text-orange-600 ml-1">| {item.cuisson}</span>}
                                      {item.sauce && <span className="text-amber-600 ml-1">| {item.sauce}</span>}
                                      {item.accompagnements?.length > 0 && <span className="text-green-600 ml-1">| {item.accompagnements.join(', ')}</span>}
                                    </span>
                                    {(staffRole === 'admin' || staffRole === 'webmaster') && <>
                                      <button onClick={() => openEditItem(order, realIdx)} className="px-1 py-0.5 rounded text-[8px] font-bold bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200" data-testid={`edit-item-${order.id}-${realIdx}`}><Pencil size={7} /></button>
                                      <button onClick={() => deleteOrderItem(order.id, realIdx)} className="px-1 py-0.5 rounded text-[8px] font-bold bg-red-50 text-red-400 hover:bg-red-100 border border-red-200" data-testid={`delete-item-${order.id}-${realIdx}`}><Trash2 size={7} /></button>
                                    </>}
                                    <button onClick={() => canAdvance && handleItemStatusChange(order.id, realIdx, nextStatus)}
                                      className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${sColor} ${canAdvance ? 'cursor-pointer hover:opacity-80' : 'opacity-60'}`}
                                      data-testid={`item-status-${order.id}-${realIdx}`}>{iStatus === 'Servie' ? '✓' : iStatus.split(' ').pop()}{canAdvance ? ' →' : ''}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* BOISSONS column */}
                    {hasBoissons && (
                      <div className="border rounded-lg p-2 bg-purple-50/30 border-purple-100">
                        <div className="text-[10px] font-bold text-purple-700 uppercase tracking-wider mb-1 flex items-center gap-1"><Wine size={10} /> Boissons</div>
                        {tableOrders.map(order => {
                          const boissons = order.items.filter(i => i.type === 'boisson');
                          if (!boissons.length) return null;
                          return (
                            <div key={order.id} className="mb-1.5 last:mb-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] font-semibold">{order.customer_name || 'Client'}</span>
                              </div>
                              {boissons.map((item, idx) => {
                                const realIdx = order.items.indexOf(item);
                                const iStatus = item.status || 'En attente';
                                const sColor = iStatus === 'Servie' ? 'bg-blue-100 text-blue-700' : iStatus === 'Prête' ? 'bg-green-100 text-green-700' : iStatus === 'En préparation' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700';
                                const nextStatus = STATUSES[Math.min(STATUSES.indexOf(iStatus) + 1, STATUSES.length - 1)];
                                const canAdvance = iStatus !== 'Servie';
                                return (
                                  <div key={idx} className="flex items-center gap-1 text-[10px] text-gray-700 pl-2 border-l-2 border-purple-200 ml-0.5">
                                    <span className="flex-1">{item.quantity}x {item.name}</span>
                                    {(staffRole === 'admin' || staffRole === 'webmaster') && <>
                                      <button onClick={() => openEditItem(order, realIdx)} className="px-1 py-0.5 rounded text-[8px] font-bold bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200" data-testid={`edit-item-${order.id}-${realIdx}`}><Pencil size={7} /></button>
                                      <button onClick={() => deleteOrderItem(order.id, realIdx)} className="px-1 py-0.5 rounded text-[8px] font-bold bg-red-50 text-red-400 hover:bg-red-100 border border-red-200" data-testid={`delete-item-${order.id}-${realIdx}`}><Trash2 size={7} /></button>
                                    </>}
                                    <button onClick={() => canAdvance && handleItemStatusChange(order.id, realIdx, nextStatus)}
                                      className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${sColor} ${canAdvance ? 'cursor-pointer hover:opacity-80' : 'opacity-60'}`}
                                      data-testid={`item-status-${order.id}-${realIdx}`}>{iStatus === 'Servie' ? '✓' : iStatus.split(' ').pop()}{canAdvance ? ' →' : ''}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Per-order action bar */}
                  <div className="space-y-1 mt-2">
                    {tableOrders.map(order => (
                      <div key={order.id} className="flex items-center gap-1 flex-wrap bg-gray-50 rounded px-2 py-1" data-testid={`order-row-${order.id}`}>
                        <span className="text-[10px] font-semibold w-20 truncate">{order.customer_name || 'Client'}</span>
                        <Badge variant={getStatusColor(order.status)} className="text-[8px] h-4">{order.status}</Badge>
                        {order.drinks_with_food && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid={`drinks-badge-${order.id}`}><Beer size={8} /> Boissons avec plats</span>}
                        <span className="text-[10px] font-bold text-[#8b4513]">{order.total}&euro;</span>
                        <div className="flex-1" />
                        <Button onClick={() => openAddItems(order)} size="sm" variant="outline" className="h-5 text-[8px] px-1.5 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" data-testid={`add-items-${order.id}`}><Plus size={8} className="mr-0.5" /> Ajouter</Button>
                        {getAvailableStatuses(order.status).map(ns => {
                          const back = STATUSES.indexOf(ns) < STATUSES.indexOf(order.status);
                          return <button key={ns} onClick={() => handleStatusChange(order.id, ns)} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[8px] font-semibold ${STATUS_COLORS[ns]}`} data-testid={`status-btn-${order.id}-${ns.replace(/\s/g, '-')}`}>{back ? <Undo2 size={8} /> : <ArrowRight size={8} />}{ns}</button>;
                        })}
                        {order.status === 'Servie' && staffRole !== 'serveur' && <Button onClick={() => openPaySingleDialog(order)} size="sm" className="bg-green-600 hover:bg-green-700 text-[8px] h-5 px-1.5" data-testid={`pay-single-${order.id}`}><DollarSign size={10} /> Encaisser</Button>}
                        {order.status === 'En attente' && (staffRole === 'admin' || staffRole === 'webmaster') && <button onClick={() => handleCancelOrder(order.id)} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[8px] font-semibold bg-red-50 text-red-600 border-red-300" data-testid={`cancel-order-${order.id}`}><XCircle size={8} /> Annuler</button>}
                        {order.items.some(i => i.type !== 'boisson') && <Button onClick={() => printTicket(order, 'cuisine')} variant="ghost" size="sm" className="h-5 text-[8px] px-1"><ChefHat size={8} /></Button>}
                        {order.items.some(i => i.type === 'boisson') && <Button onClick={() => printTicket(order, 'boissons')} variant="ghost" size="sm" className="h-5 text-[8px] px-1"><Printer size={8} /></Button>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {filteredTables.length === 0 && <div className="text-center py-12"><p className="text-lg text-gray-400">Aucune commande</p></div>}
      </div>

      {/* Payment Dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{payMode === 'all' ? `Encaissement Table ${payingOrders[0]?.table_number}` : `Encaissement - ${payingOrder?.customer_name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-gray-50 p-3 rounded-lg">
              {payMode === 'all' && payingOrders.map(o => <div key={o.id} className="flex justify-between text-xs py-0.5"><span>{o.customer_name}</span><span>{o.total}&euro;</span></div>)}
              <p className="text-2xl font-bold text-[#8b4513] border-t mt-1 pt-1">{currentTotal.toFixed(2)} &euro;</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1.5">Mode de paiement</p>
              <div className="grid grid-cols-3 gap-1">
                {activeMethods.map(m => {
                  const Icon = ICON_MAP[m.icon] || CreditCard;
                  return <button key={m.method_id} data-testid={`pay-method-${m.method_id}`} onClick={() => { setPayMethod(m.method_id); setMixedAmounts({}); }}
                    className={`flex items-center gap-1 p-1.5 rounded-lg border text-[10px] font-medium transition-all ${payMethod === m.method_id ? 'bg-[#2c1810] text-white border-[#2c1810]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                    <Icon size={12} /> {m.label}
                  </button>;
                })}
                <button data-testid="pay-method-Mixte" onClick={() => { setPayMethod('Mixte'); setMixedAmounts({}); }}
                  className={`flex items-center gap-1 p-1.5 rounded-lg border text-[10px] font-medium transition-all ${payMethod === 'Mixte' ? 'bg-[#2c1810] text-white border-[#2c1810]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                  <DollarSign size={12} /> Mixte
                </button>
              </div>
            </div>
            {cashMethodIds.includes(payMethod) && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Montant recu</p>
                <Input type="number" step="0.01" value={amountGiven} onChange={e => setAmountGiven(e.target.value)} placeholder="0.00" className="h-9 text-lg font-bold" data-testid="amount-given-input" />
                {changeDue && <div className="mt-1.5 bg-green-50 border border-green-200 rounded p-2 text-center"><p className="text-xs text-green-600">Monnaie</p><p className="text-lg font-bold text-green-700">{changeDue} &euro;</p></div>}
              </div>
            )}
            {payMethod === 'Mixte' && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-700">Ventilation du paiement</p>
                {activeMethods.map(m => (
                  <div key={m.method_id} className="flex items-center gap-2">
                    <span className="text-[10px] font-medium w-28 truncate">{m.label}</span>
                    <Input type="number" step="0.01" value={mixedAmounts[m.method_id] || ''} onChange={e => setMixedAmounts({ ...mixedAmounts, [m.method_id]: e.target.value })} placeholder="0.00" className="h-7 text-sm flex-1" data-testid={`mixed-${m.method_id}`} />
                    <span className="text-[10px] text-gray-400">&euro;</span>
                  </div>
                ))}
                <div className={`text-xs font-bold text-right ${parseFloat(mixedRemaining) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                  {parseFloat(mixedRemaining) > 0.01 ? `Reste: ${mixedRemaining} EUR` : 'Complet'}
                </div>
              </div>
            )}
            <Button data-testid="confirm-payment-btn" onClick={handlePayment} disabled={!payMethod || processing}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-9 font-semibold disabled:opacity-40">
              {processing ? 'Traitement...' : 'Confirmer le paiement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simple Invoice Dialog */}
      <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Facture Simplifiee</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-gray-500">Envoi d'une facture sans detail - juste une designation et le montant.</p>
            <div>
              <label className="text-xs font-semibold text-gray-700">Email du client *</label>
              <Input value={invoiceData.email} onChange={e => setInvoiceData({ ...invoiceData, email: e.target.value })} placeholder="client@email.com" className="mt-0.5" data-testid="invoice-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Nom du client</label>
              <Input value={invoiceData.client_name} onChange={e => setInvoiceData({ ...invoiceData, client_name: e.target.value })} placeholder="M. Dupont" className="mt-0.5" data-testid="invoice-client" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Designation *</label>
              <Input value={invoiceData.designation} onChange={e => setInvoiceData({ ...invoiceData, designation: e.target.value })} placeholder="Ex: 1 repas, 2 repas..." className="mt-0.5" data-testid="invoice-designation" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Montant (EUR)</label>
              <Input type="number" step="0.01" value={invoiceData.amount} onChange={e => setInvoiceData({ ...invoiceData, amount: parseFloat(e.target.value) || 0 })} className="mt-0.5 text-lg font-bold" data-testid="invoice-amount" />
            </div>
            <Button onClick={sendSimpleInvoice} disabled={sendingInvoice} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white h-9 font-semibold" data-testid="send-invoice-btn">
              <Mail size={14} className="mr-1" /> {sendingInvoice ? 'Envoi...' : 'Envoyer la facture'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-sm" data-testid="edit-item-dialog">
          <DialogHeader><DialogTitle className="text-sm">Modifier l'article</DialogTitle></DialogHeader>
          {editOrder && editItemIdx !== null && editOrder.items[editItemIdx] && (
            <div className="space-y-3 mt-2">
              <p className="text-sm font-semibold">{editOrder.items[editItemIdx].name}</p>
              <p className="text-xs text-gray-500">Prix unitaire: {editOrder.items[editItemIdx].price}&euro;</p>
              {editOrder.items[editItemIdx].cuisson && <p className="text-[10px] text-orange-600">Cuisson: {editOrder.items[editItemIdx].cuisson}</p>}
              {editOrder.items[editItemIdx].sauce && <p className="text-[10px] text-amber-600">Sauce: {editOrder.items[editItemIdx].sauce}</p>}
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Quantite</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setEditQty(Math.max(0, editQty - 1))} className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"><Minus size={14} /></button>
                  <span className="text-xl font-bold w-8 text-center">{editQty}</span>
                  <button onClick={() => setEditQty(editQty + 1)} className="w-8 h-8 rounded-full bg-[#8B0000] text-white flex items-center justify-center hover:bg-[#6d0000]"><Plus size={14} /></button>
                </div>
                {editQty === 0 && <p className="text-[10px] text-red-500 mt-1">L'article sera supprime</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Notes</label>
                <Input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Notes..." className="h-8 text-xs" data-testid="edit-notes" />
              </div>
              <Button onClick={saveEditItem} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white" data-testid="save-edit-btn">
                {editQty === 0 ? 'Supprimer l\'article' : 'Enregistrer'}
              </Button>
              {(staffRole === 'admin' || staffRole === 'webmaster') && (
                <>
                  <div className="border-t pt-2">
                    <button onClick={() => { setShowReplaceItem(!showReplaceItem); setReplaceCat(menuCategories[0]?.id || null); }} className="text-xs text-blue-600 hover:underline font-semibold flex items-center gap-1" data-testid="replace-item-btn">
                      <Pencil size={10} /> Remplacer par un autre plat
                    </button>
                  </div>
                  {showReplaceItem && (
                    <div className="border rounded-lg p-2 bg-gray-50 max-h-[200px] overflow-y-auto">
                      <div className="flex gap-1 overflow-x-auto pb-1 mb-1">
                        {menuCategories.map(cat => (
                          <button key={cat.id} onClick={() => setReplaceCat(cat.id)}
                            className={`whitespace-nowrap px-2 py-0.5 rounded-full text-[9px] font-semibold ${replaceCat === cat.id ? 'bg-[#8B0000] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                            {cat.name.replace('Nos ', '')}
                          </button>
                        ))}
                      </div>
                      <div className="space-y-0.5">
                        {menuCategories.find(c => c.id === replaceCat)?.items?.map(item => {
                          const cat = menuCategories.find(c => c.id === replaceCat);
                          return (
                            <button key={item.id} onClick={() => replaceItemInOrder(item, cat)}
                              className="w-full flex items-center justify-between bg-white rounded px-2 py-1.5 hover:bg-blue-50 transition-colors text-left"
                              data-testid={`replace-with-${item.id}`}>
                              <span className="text-xs font-medium truncate">{item.name}</span>
                              <span className="text-xs text-[#8b4513] font-bold shrink-0 ml-2">{item.price}&euro;</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Items to Order Dialog */}
      <Dialog open={addItemsDialog} onOpenChange={setAddItemsDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="add-items-dialog">
          <DialogHeader><DialogTitle className="text-sm">Ajouter des articles - {addToOrder?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Category tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1">
              {menuCategories.map(cat => (
                <button key={cat.id} onClick={() => setSelectedAddCat(cat.id)}
                  className={`whitespace-nowrap px-2 py-1 rounded-full text-[10px] font-semibold transition-all ${
                    selectedAddCat === cat.id
                      ? (cat.isDrink ? 'bg-amber-600 text-white' : 'bg-[#8B0000] text-white')
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`add-cat-${cat.id}`}
                >
                  {cat.isDrink && <Wine size={10} className="inline mr-0.5" />}
                  {cat.name.replace('Nos ', '')}
                </button>
              ))}
            </div>
            {/* Items list */}
            <div className="space-y-1 max-h-[50vh] overflow-y-auto">
              {menuCategories.find(c => c.id === selectedAddCat)?.items?.map(item => {
                const cat = menuCategories.find(c => c.id === selectedAddCat);
                return (
                  <div key={item.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-[#8b4513] font-bold">{item.price}&euro;</p>
                    </div>
                    <Button
                      onClick={() => addItemToOrder(item, cat)}
                      disabled={addingItem}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs"
                      data-testid={`add-item-btn-${item.id}`}
                    >
                      <Plus size={12} className="mr-0.5" /> Ajouter
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManagement;
